import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SellerLayout } from '../components/SellerLayout';
import { getSale, refundSale } from '../api/client';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { colors } from '../constants/colors';
import { parseUTC } from '../utils/date';
import { Package, ArrowLeft, Clock, Check, Truck, X, User, CreditCard, AlertCircle, RefreshCw, Loader2 } from 'lucide-react';
import type { ItemSale, ItemSaleWithPayment, PaymentStatus } from '../types';
import type { LucideIcon } from 'lucide-react';
import { Img } from '../components/Img';

const STATUS_CONFIG: Record<ItemSale['status'], { label: string; color: string; bgColor: string; Icon: LucideIcon }> = {
  PENDING: { label: 'Pending Payment', color: '#F59E0B', bgColor: '#FEF3C7', Icon: Clock },
  CONFIRMED: { label: 'Needs Shipping', color: '#EC4899', bgColor: '#FCE7F3', Icon: Package },
  SHIPPED: { label: 'Shipped', color: '#3B82F6', bgColor: '#DBEAFE', Icon: Truck },
  DELIVERED: { label: 'Delivered', color: '#22C55E', bgColor: '#DCFCE7', Icon: Check },
  CANCELLED: { label: 'Cancelled', color: colors.gray[600], bgColor: colors.gray[100], Icon: X },
  VOIDED: { label: 'Voided', color: colors.gray[600], bgColor: colors.gray[100], Icon: X },
};

const PAYMENT_STATUS_CONFIG: Record<PaymentStatus, { label: string; color: string; bgColor: string; Icon: LucideIcon }> = {
  PENDING: { label: 'Payment Pending', color: '#F59E0B', bgColor: '#FEF3C7', Icon: Clock },
  AUTHORIZED: { label: 'Authorized', color: '#3B82F6', bgColor: '#DBEAFE', Icon: CreditCard },
  CAPTURED: { label: 'Captured', color: '#22C55E', bgColor: '#DCFCE7', Icon: Check },
  FAILED: { label: 'Payment Failed', color: '#DC2626', bgColor: '#FEE2E2', Icon: AlertCircle },
  CANCELLED: { label: 'Cancelled', color: colors.gray[600], bgColor: colors.gray[100], Icon: X },
  REFUNDED: { label: 'Refunded', color: '#8B5CF6', bgColor: '#EDE9FE', Icon: RefreshCw },
  PARTIALLY_REFUNDED: { label: 'Partially Refunded', color: '#8B5CF6', bgColor: '#EDE9FE', Icon: RefreshCw },
  VOIDED: { label: 'Payment Voided', color: colors.gray[600], bgColor: colors.gray[100], Icon: X },
};

function formatDate(dateString: string): string {
  const date = parseUTC(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

export function SellerSaleDetailPage() {
  const { saleId } = useParams<{ saleId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<ItemSaleWithPayment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefunding, setIsRefunding] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  useDocumentTitle(`Sale #${order?.id ?? ''}`);

  useEffect(() => {
    if (saleId) {
      loadSale();
    }
  }, [saleId]);

  const loadSale = async () => {
    try {
      setIsLoading(true);
      const data = await getSale(saleId!);
      setOrder(data as ItemSaleWithPayment);
      // Set default refund amount to sale total
      setRefundAmount((data as ItemSaleWithPayment).captureAmount?.toString() || data.total.toString());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sale');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefund = async () => {
    if (!saleId) return;
    try {
      setIsRefunding(true);
      const amount = parseFloat(refundAmount);
      const updatedOrder = await refundSale(saleId, amount);
      setOrder(updatedOrder);
      setShowRefundModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process refund');
    } finally {
      setIsRefunding(false);
    }
  };

  if (isLoading) {
    return (
      <SellerLayout>
        <div style={styles.loading}>Loading sale...</div>
      </SellerLayout>
    );
  }

  if (error || !order) {
    return (
      <SellerLayout>
        <div style={styles.error}>{error || 'Sale not found'}</div>
      </SellerLayout>
    );
  }

  const statusConfig = STATUS_CONFIG[order.status];

  return (
    <SellerLayout>
      <button onClick={() => navigate(-1)} style={styles.backButton}>
        <ArrowLeft size={20} />
        <span>Back</span>
      </button>

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Sale #{order.id.slice(0, 8)}</h1>
          <p style={styles.orderDate}>{formatDate(order.createdAt)}</p>
        </div>
        <div
          style={{
            ...styles.statusBadge,
            backgroundColor: statusConfig.bgColor,
            color: statusConfig.color,
          }}
        >
          <statusConfig.Icon size={16} />
          <span>{statusConfig.label}</span>
        </div>
      </div>

      <div style={styles.grid}>
        <div style={styles.mainColumn}>
          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>Item</h2>
            <div style={styles.itemRow}>
              {order.itemSnapshot?.imageUrl ? (
                <Img
                  src={order.itemSnapshot.imageUrl}
                  alt={order.itemSnapshot.name}
                  variant="thumb"
                  renderedWidth={80}
                  blurhash={order.itemSnapshot.blurhash}
                  style={styles.itemImage}
                />
              ) : (
                <div style={styles.itemImagePlaceholder}>
                  <Package size={32} color={colors.gray[300]} />
                </div>
              )}
              <div style={styles.itemInfo}>
                <h3 style={styles.itemName}>{order.itemSnapshot?.name ?? 'Unknown Item'}</h3>
                {order.itemSnapshot?.description && (
                  <p style={styles.itemDescription}>{order.itemSnapshot.description}</p>
                )}
              </div>
            </div>
          </div>

          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>Buyer</h2>
            <div style={styles.buyerRow}>
              <div style={styles.buyerAvatar}>
                <User size={20} color={colors.gray[400]} />
              </div>
              <div>
                <p style={styles.buyerId}>{order.buyerUsername ?? `Buyer ID: ${order.buyerId.slice(0, 8)}...`}</p>
              </div>
            </div>
          </div>

        </div>

        <div style={styles.sideColumn}>
          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>Sale Summary</h2>
            <div style={styles.summaryRow}>
              <span>Winning Bid</span>
              <span>{formatCurrency(order.winningBid)}</span>
            </div>
            <div style={styles.summaryRow}>
              <span>Shipping</span>
              <span>{formatCurrency(order.shippingCost)}</span>
            </div>
            <div style={styles.summaryRow}>
              <span>Tax</span>
              <span>{formatCurrency(order.tax)}</span>
            </div>
            <div style={styles.divider} />
            <div style={styles.totalRow}>
              <span>Total Charged</span>
              <span>{formatCurrency(order.captureAmount || order.total)}</span>
            </div>
          </div>

          {/* Fee Breakdown */}
          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>Fee Breakdown</h2>
            <div style={styles.summaryRow}>
              <span>Subtotal</span>
              <span>{formatCurrency(order.captureAmount || order.total)}</span>
            </div>
            {order.platformFee !== undefined && (
              <div style={styles.summaryRow}>
                <span>Platform Fee</span>
                <span style={{ color: '#DC2626' }}>-{formatCurrency(order.platformFee)}</span>
              </div>
            )}
            {order.processingFee !== undefined && (
              <div style={styles.summaryRow}>
                <span>Processing Fee</span>
                <span style={{ color: '#DC2626' }}>-{formatCurrency(order.processingFee)}</span>
              </div>
            )}
            <div style={styles.divider} />
            <div style={styles.earningsRow}>
              <span>Your Earnings</span>
              <span style={styles.earningsValue}>
                {formatCurrency(order.sellerNet || order.winningBid)}
              </span>
            </div>
            {order.refundedAmount !== undefined && order.refundedAmount > 0 && (
              <div style={{ ...styles.summaryRow, marginTop: '12px' }}>
                <span>Refunded</span>
                <span style={{ color: '#8B5CF6' }}>-{formatCurrency(order.refundedAmount)}</span>
              </div>
            )}
          </div>

          {/* Payment Status */}
          {order.paymentStatus && (
            <div style={styles.card}>
              <h2 style={styles.sectionTitle}>Payment Status</h2>
              {(() => {
                const paymentConfig = PAYMENT_STATUS_CONFIG[order.paymentStatus];
                return (
                  <div
                    style={{
                      ...styles.paymentStatusBadge,
                      backgroundColor: paymentConfig.bgColor,
                      color: paymentConfig.color,
                    }}
                  >
                    <paymentConfig.Icon size={16} />
                    <span>{paymentConfig.label}</span>
                  </div>
                );
              })()}
              {order.paymentStatus === 'CAPTURED' && (
                <>
                  <div style={styles.divider} />
                  <button
                    style={styles.refundButton}
                    onClick={() => setShowRefundModal(true)}
                  >
                    <RefreshCw size={18} />
                    Issue Refund
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Refund Modal */}
        {showRefundModal && (
          <div style={styles.modalOverlay} onClick={() => setShowRefundModal(false)}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
              <h2 style={styles.modalTitle}>Issue Refund</h2>
              <p style={styles.modalText}>
                Enter the amount to refund. Maximum: {formatCurrency(order.captureAmount || order.total)}
              </p>
              <div style={styles.inputGroup}>
                <label style={styles.inputLabel}>Refund Amount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={order.captureAmount || order.total}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.modalActions}>
                <button
                  style={styles.cancelButton}
                  onClick={() => setShowRefundModal(false)}
                  disabled={isRefunding}
                >
                  Cancel
                </button>
                <button
                  style={styles.confirmRefundButton}
                  onClick={handleRefund}
                  disabled={isRefunding || !refundAmount || parseFloat(refundAmount) <= 0}
                >
                  {isRefunding ? (
                    <>
                      <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                      Processing...
                    </>
                  ) : (
                    `Refund ${formatCurrency(parseFloat(refundAmount) || 0)}`
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SellerLayout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  loading: {
    textAlign: 'center',
    padding: '48px',
    color: colors.gray[600],
  },
  error: {
    textAlign: 'center',
    padding: '48px',
    color: colors.accent.pink,
    backgroundColor: '#FEE2E2',
    borderRadius: '12px',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 0',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.gray[600],
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    marginBottom: '16px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    color: colors.ink.black,
    margin: '0 0 4px 0',
  },
  orderDate: {
    fontSize: '14px',
    color: colors.gray[600],
    margin: 0,
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    borderRadius: '100px',
    fontSize: '14px',
    fontWeight: 600,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 320px',
    gap: '20px',
  },
  mainColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  sideColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  card: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    padding: '20px',
    border: `1px solid ${colors.gray[300]}`,
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 700,
    color: colors.gray[600],
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '0 0 16px 0',
  },
  itemRow: {
    display: 'flex',
    gap: '16px',
  },
  itemImage: {
    width: '80px',
    height: '80px',
    borderRadius: '12px',
    objectFit: 'cover',
  },
  itemImagePlaceholder: {
    width: '80px',
    height: '80px',
    borderRadius: '12px',
    backgroundColor: colors.gray[100],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  itemInfo: {
    flex: 1,
  },
  itemName: {
    fontSize: '16px',
    fontWeight: 600,
    color: colors.ink.black,
    margin: '0 0 8px 0',
  },
  itemDescription: {
    fontSize: '14px',
    color: colors.gray[600],
    margin: 0,
  },
  buyerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  buyerAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: colors.gray[100],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buyerId: {
    fontSize: '14px',
    color: colors.gray[700],
    margin: 0,
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '14px',
    color: colors.gray[700],
    marginBottom: '12px',
  },
  divider: {
    height: '1px',
    backgroundColor: colors.gray[300],
    margin: '12px 0',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '16px',
    fontWeight: 700,
    color: colors.ink.black,
  },
  earningsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '14px',
    color: colors.gray[700],
  },
  earningsValue: {
    fontWeight: 600,
    color: '#22C55E',
  },
  earningsNote: {
    fontSize: '12px',
    color: colors.gray[400],
    margin: '8px 0 0 0',
  },
  paymentStatusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    borderRadius: '100px',
    fontSize: '14px',
    fontWeight: 600,
  },
  refundButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#8B5CF6',
    backgroundColor: '#EDE9FE',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    padding: '24px',
    width: '100%',
    maxWidth: '400px',
    margin: '20px',
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: 700,
    color: colors.ink.black,
    margin: '0 0 8px 0',
  },
  modalText: {
    fontSize: '14px',
    color: colors.gray[600],
    margin: '0 0 20px 0',
  },
  inputGroup: {
    marginBottom: '20px',
  },
  inputLabel: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.gray[700],
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    fontSize: '16px',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '10px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  modalActions: {
    display: 'flex',
    gap: '12px',
  },
  cancelButton: {
    flex: 1,
    padding: '12px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.gray[700],
    backgroundColor: colors.gray[100],
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  confirmRefundButton: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: '#8B5CF6',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
};
